import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { PlayArrow, Stop, CheckCircle, ArrowBack } from '@mui/icons-material';
import { getApiUrl } from '../../utils/apiConfig';

export default function MaScanPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(null);
  const [result, setResult] = useState(null);
  const [selectedAction, setSelectedAction] = useState('checkin');
  const [manualForm, setManualForm] = useState({
    wirksam_am: new Date().toISOString().slice(0, 10),
    dauer_minuten: '',
    taetigkeit: '',
    notiz: ''
  });

  const api = useMemo(() => axios.create({ withCredentials: true }), []);

  const loadScanInfo = useCallback(async () => {
    if (!token) {
      setError('Kein Scan-Code in der URL gefunden');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(getApiUrl('/api/ma/scan'), { params: { token } });
      if (!data?.success) {
        throw new Error(data?.error || 'Scan-Code konnte nicht geprueft werden');
      }
      setInfo(data);
      setSelectedAction(data?.suggested_action || data?.action || 'checkin');
      setManualForm((prev) => ({ ...prev, taetigkeit: data?.task?.titel || prev.taetigkeit }));
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Scan-Code konnte nicht geprueft werden');
    } finally {
      setLoading(false);
    }
  }, [api, token]);

  useEffect(() => {
    loadScanInfo();
  }, [loadScanInfo]);

  const runAction = async (action, payload = {}) => {
    setSubmitting(true);
    setError('');
    try {
      const { data } = await api.post(getApiUrl('/api/ma/scan'), {
        token,
        action,
        ...payload
      });
      if (!data?.success) {
        throw new Error(data?.error || 'Aktion fehlgeschlagen');
      }
      setResult(data);
      await loadScanInfo();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Aktion fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveManual = async () => {
    const minutes = Number(manualForm.dauer_minuten || 0);
    if (!Number.isFinite(minutes) || minutes <= 0) {
      setError('Bitte eine gueltige Dauer in Minuten eingeben');
      return;
    }

    await runAction('manual', {
      wirksam_am: manualForm.wirksam_am,
      dauer_minuten: Math.round(minutes),
      taetigkeit: manualForm.taetigkeit,
      notiz: manualForm.notiz
    });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 460 }}>
        <CardContent>
          <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center' }}>
            {loading ? (
              <CircularProgress />
            ) : result ? (
              <>
                <CheckCircle color="success" sx={{ fontSize: 64 }} />
                <Typography variant="h5">{result.action === 'checkin' ? 'Eingestempelt' : result.action === 'checkout' ? 'Ausgestempelt' : 'Zeit nachgetragen'}</Typography>
                <Typography color="text.secondary">{result.task?.titel}</Typography>
                {result.action === 'checkout' && result.timer?.dauer_minuten != null && (
                  <Chip label={`Dauer: ${Math.floor(result.timer.dauer_minuten / 60)}h ${result.timer.dauer_minuten % 60}min`} />
                )}
                {result.stopped_previous && (
                  <Alert severity="info" sx={{ width: '100%' }}>
                    Vorheriger Timer wurde automatisch gestoppt
                    {result.stopped_previous.dauer_minuten != null ? ` (${result.stopped_previous.dauer_minuten} min)` : ''}.
                  </Alert>
                )}
                <Button startIcon={<ArrowBack />} onClick={() => navigate('/ma', { replace: true })}>
                  Zum Portal
                </Button>
              </>
            ) : (
              <>
                <Typography variant="overline" color="text.secondary">Zeiterfassung</Typography>
                <Typography variant="h5">Task-Login</Typography>
                {info?.task && <Typography color="text.secondary">{info.task.titel}</Typography>}
                {error ? <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert> : null}

                {info?.timer && !info?.timer_on_this_task ? (
                  <Alert severity="warning" sx={{ width: '100%' }}>
                    Es laeuft bereits ein Timer auf einem anderen Task. Beim Start auf diesem Task wird er automatisch gestoppt.
                  </Alert>
                ) : null}
                {info?.timer_on_this_task ? (
                  <Alert severity="info" sx={{ width: '100%' }}>
                    Fuer diesen Task laeuft bereits ein Timer.
                  </Alert>
                ) : null}

                <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                  <Button fullWidth size="small" variant={selectedAction === 'checkin' ? 'contained' : 'outlined'} color="success" onClick={() => setSelectedAction('checkin')}>
                    Start
                  </Button>
                  <Button fullWidth size="small" variant={selectedAction === 'checkout' ? 'contained' : 'outlined'} color="error" onClick={() => setSelectedAction('checkout')}>
                    Ende
                  </Button>
                  <Button fullWidth size="small" variant={selectedAction === 'manual' ? 'contained' : 'outlined'} onClick={() => setSelectedAction('manual')}>
                    Nachtragen
                  </Button>
                </Stack>

                {selectedAction === 'checkin' ? (
                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    color="success"
                    startIcon={<PlayArrow />}
                    disabled={submitting || info?.timer_on_this_task}
                    onClick={() => runAction('checkin')}
                    sx={{ py: 1.5, fontSize: '1.05rem' }}
                  >
                    {submitting ? 'Bitte warten...' : 'Jetzt einstempeln'}
                  </Button>
                ) : null}

                {selectedAction === 'checkout' ? (
                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    color="error"
                    startIcon={<Stop />}
                    disabled={submitting || !info?.timer_on_this_task}
                    onClick={() => runAction('checkout')}
                    sx={{ py: 1.5, fontSize: '1.05rem' }}
                  >
                    {submitting ? 'Bitte warten...' : 'Jetzt ausstempeln'}
                  </Button>
                ) : null}

                {selectedAction === 'manual' ? (
                  <Stack spacing={1} sx={{ width: '100%' }}>
                    <TextField
                      label="Datum"
                      type="date"
                      value={manualForm.wirksam_am}
                      onChange={(e) => setManualForm((prev) => ({ ...prev, wirksam_am: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <TextField
                      label="Dauer (Minuten)"
                      type="number"
                      value={manualForm.dauer_minuten}
                      onChange={(e) => setManualForm((prev) => ({ ...prev, dauer_minuten: e.target.value }))}
                      fullWidth
                    />
                    <TextField
                      label="Taetigkeit"
                      value={manualForm.taetigkeit}
                      onChange={(e) => setManualForm((prev) => ({ ...prev, taetigkeit: e.target.value }))}
                      fullWidth
                    />
                    <TextField
                      label="Notiz"
                      value={manualForm.notiz}
                      onChange={(e) => setManualForm((prev) => ({ ...prev, notiz: e.target.value }))}
                      fullWidth
                      multiline
                      minRows={2}
                    />
                    <Button
                      fullWidth
                      size="large"
                      variant="contained"
                      disabled={submitting}
                      onClick={handleSaveManual}
                      sx={{ py: 1.5, fontSize: '1.05rem' }}
                    >
                      {submitting ? 'Bitte warten...' : 'Zeit nachtragen'}
                    </Button>
                  </Stack>
                ) : null}

                <Button startIcon={<ArrowBack />} onClick={() => navigate('/ma', { replace: true })}>
                  Zum Portal
                </Button>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
